import { FormattedMessage, useIntl } from 'react-intl';

import addOnStore from 'Models/AddOnStore';
import { CapacityCategory } from 'Models/Shared/Capacity/CapacityCategory';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { Colors, Display } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { ReactNode } from 'react';
import { OverbookingWarningDto } from './OverbookingWarningDto';

const OverbookingMessages = {
	genericNestedVehicle: 'reservation__overbooking-modal__generic-nested-vehicle-message',
	vehicle: 'reservation__overbooking-modal__vehicle-message',
	trailer: 'reservation__overbooking-modal__trailer-message',
	weight: 'reservation__overbooking-modal__weight-message',
	passenger: 'reservation__overbooking-modal__passenger-message',
	passengerClass: 'reservation__overbooking-modal__passenger-class-message',
	addOn: 'reservation__overbooking-modal__add-on-message',
	addOnTitle: 'reservation__overbooking-modal__add-on-title',
};

const formattedMessages = {
	customerTitle: 'reservation__overbooking-modal__title-for-customer',
	noInitialCapacityTitle: 'reservation__overbooking-modal__no-initial-capacity-title',
	selectNewTicket: 'reservation__overbooking-modal__select-new-ticket-text',
	back: 'reservation__overbooking-modal__back-text',
} as const;

interface Props {
	onClickCross: (capacityCategory: CapacityCategory) => void;
	onClickSelect: (capacityCategory: CapacityCategory) => void;
	departingOverbookingWarnings?: OverbookingWarningDto[];
	returningOverbookingWarnings?: OverbookingWarningDto[];
}

const convertCapacityState = (
	departingOverbookingWarnings?: OverbookingWarningDto[],
	returningOverbookingWarnings?: OverbookingWarningDto[],
) => {
	if (departingOverbookingWarnings && departingOverbookingWarnings.length > 0) {
		return departingOverbookingWarnings[0].overbookingCause;
	}
	if (returningOverbookingWarnings && returningOverbookingWarnings.length > 0) {
		return returningOverbookingWarnings[0].overbookingCause;
	}
	return undefined;
};

/** This component chooses what capacity type modal to display. */
export default function OverbookingModal({
	onClickCross,
	onClickSelect,
	departingOverbookingWarnings,
	returningOverbookingWarnings,
}: Props) {
	const intl = useIntl();

	// There can be multiple overbooking warnings,
	// but we will only be showing the first warning.
	const capacityTypeException = convertCapacityState(departingOverbookingWarnings, returningOverbookingWarnings);

	if (capacityTypeException === undefined) {
		return <></>;
	}

	const allWarnings = [departingOverbookingWarnings, returningOverbookingWarnings];

	// Show Nested warnings first, if any
	for (const warnings of allWarnings) {
		if (warnings && warnings.length > 0) {
			const { nestedCapacityInfo } = warnings[0];
			if (nestedCapacityInfo) {
				const messageId = nestedCapacityInfo.errorMessageId ?? 'missing-error-message-id';
				const defaultMessage: string = intl.formatMessage({
					id: OverbookingMessages.genericNestedVehicle,
					defaultMessage: 'Your vehicle cannot fit on the ferry.',
				});
				const errorMessage = intl.formatMessage({ id: messageId, defaultMessage });
				// A "no initial capacity" warning (the ferry was never given capacity for this
				// restriction) gets its own heading, distinct from the "no capacity remaining" one.
				const title = nestedCapacityInfo.isNoInitialCapacity ? (
					<FormattedMessage
						id={formattedMessages.noInitialCapacityTitle}
						defaultMessage="Not available on this trip"
					/>
				) : undefined;
				return (
					<GenericOverbookingModal
						title={title}
						onCancel={() => onClickCross(capacityTypeException)}
						onConfirm={() => onClickSelect(capacityTypeException)}
					>
						{errorMessage}
					</GenericOverbookingModal>
				);
			}
		}
	}

	switch (capacityTypeException) {
		case 'Vehicle': {
			return (
				<GenericOverbookingModal
					onCancel={() => onClickCross(capacityTypeException)}
					onConfirm={() => onClickSelect(capacityTypeException)}
				>
					<FormattedMessage
						id={OverbookingMessages.vehicle}
						defaultMessage="Your vehicle length exceeds the space available on the ferry."
					/>
				</GenericOverbookingModal>
			);
		}
		case 'Trailer': {
			return (
				<GenericOverbookingModal
					onCancel={() => onClickCross(capacityTypeException)}
					onConfirm={() => onClickSelect(capacityTypeException)}
				>
					<FormattedMessage
						id={OverbookingMessages.trailer}
						defaultMessage="Your trailer length exceeds the space available on the ferry."
					/>
				</GenericOverbookingModal>
			);
		}
		case 'Weight': {
			return (
				<GenericOverbookingModal
					onCancel={() => onClickCross(capacityTypeException)}
					onConfirm={() => onClickSelect(capacityTypeException)}
				>
					<FormattedMessage
						id={OverbookingMessages.weight}
						defaultMessage="Your vehicle weight exceeds the space available on this ferry so you will need to select another time slot."
					/>
				</GenericOverbookingModal>
			);
		}
		case 'Passenger': {
			return (
				<GenericOverbookingModal
					onCancel={() => onClickCross(capacityTypeException)}
					onConfirm={() => onClickSelect(capacityTypeException)}
				>
					<FormattedMessage
						id={OverbookingMessages.passenger}
						defaultMessage="Please select another time slot that has enough remaining spaces."
					/>
				</GenericOverbookingModal>
			);
		}
		// A class filling up is a different message from the ferry filling up: the trip still has room,
		// just not in the class this booking asked for, so the useful suggestion is to change class
		// rather than to change sailing.
		case 'PassengerClass': {
			return (
				<GenericOverbookingModal
					onCancel={() => onClickCross(capacityTypeException)}
					onConfirm={() => onClickSelect(capacityTypeException)}
				>
					<FormattedMessage
						id={OverbookingMessages.passengerClass}
						defaultMessage="This fare type is full on the sailing you chose. Please pick another fare type, or another time slot."
					/>
				</GenericOverbookingModal>
			);
		}
		case 'AddOn': {
			const departureAddOnsChanged = departingOverbookingWarnings?.filter(x => isNotNullOrUndefined(x.addOnId));
			const returnAddOnsChanged = returningOverbookingWarnings?.filter(x => isNotNullOrUndefined(x.addOnId));

			return (
				<GenericOverbookingModal
					onCancel={() => onClickCross(capacityTypeException)}
					onConfirm={() => onClickSelect(capacityTypeException)}
				>
					<FormattedMessage
						id={OverbookingMessages.addOn}
						defaultMessage="The following items have changed availability:"
					/>
					<If condition={isNotNullOrUndefined(departureAddOnsChanged) && departureAddOnsChanged.length > 0}>
						<div className="space-modal__body-section">
							<h6>
								<FormattedMessage
									id="reservation__overbooking-modal__add-on__departure-heading"
									defaultMessage="Departure trip"
								/>
							</h6>
							{departureAddOnsChanged?.map(x => {
								if (!x.addOnId) return null;
								return <li key={`departure-${x.addOnId}`}>{addOnStore.findAddOn(x.addOnId)?.name}</li>;
							})}
						</div>
					</If>
					<If condition={isNotNullOrUndefined(returnAddOnsChanged) && returnAddOnsChanged.length > 0}>
						<div className="space-modal__body-section">
							<h6>
								<FormattedMessage
									id="reservation__overbooking-modal__add-on__returning-heading"
									defaultMessage="Returning trip"
								/>
							</h6>
							{returnAddOnsChanged?.map(x => {
								if (!x.addOnId) return null;
								return <li key={`return-${x.addOnId}`}>{addOnStore.findAddOn(x.addOnId)?.name}</li>;
							})}
						</div>
					</If>
				</GenericOverbookingModal>
			);
		}
		default: {
			console.error('Overbooking type is invalid.');
		}
	}

	return <></>;
}

interface GenericOverbookingModalProps {
	children?: ReactNode;
	onCancel: () => void;
	onConfirm: () => void;
	/** Optional custom heading. Defaults to the generic "no capacity remaining" title. */
	title?: ReactNode;
}

function GenericOverbookingModal({ children, onCancel, onConfirm, title }: GenericOverbookingModalProps) {
	return (
		<NewModal isOpen label="Overbooked">
			<NewModal.Header>
				<NewModal.Title>
					{title ?? (
						<FormattedMessage
							id={formattedMessages.customerTitle}
							defaultMessage="Unable to add this booking"
						/>
					)}
				</NewModal.Title>
			</NewModal.Header>
			<NewModal.Body>{children}</NewModal.Body>
			<NewModal.Footer>
				<NewModal.CancelButton display={Display.Outline} colors={Colors.Grey} onClick={onCancel}>
					<FormattedMessage id={formattedMessages.back} defaultMessage="Back" />
				</NewModal.CancelButton>
				<NewModal.ConfirmButton onClick={onConfirm}>
					<FormattedMessage id={formattedMessages.selectNewTicket} defaultMessage="Select a new ticket" />
				</NewModal.ConfirmButton>
			</NewModal.Footer>
		</NewModal>
	);
}
